/**
 * Lazy-Loader mit Auto-Recovery für stale Chunk-Hashes nach Deploy.
 *
 * Problem: Vite produziert content-hashed Chunk-Filenames (z.B.
 * `consultants-list-C7tYfM_r.js`). Bei jedem Deploy ändern sich die Hashes.
 * User mit altem `index.html` im Browser-Speicher haben Verweise auf alte
 * Chunk-Pfade — die werden nach dem Deploy 404. React.lazy() wirft dann
 * "Failed to fetch dynamically imported module".
 *
 * Lösung: Diesen Wrapper statt React.lazy nutzen. Bei einem Chunk-Load-
 * Fehler wird einmalig `window.location.reload()` aufgerufen — das holt
 * das frische `index.html` mit aktuellen Chunk-Referenzen. 10-Sekunden-
 * Debounce verhindert Reload-Loops bei echten persistenten Fehlern.
 */

import { type ComponentType, type LazyExoticComponent, lazy } from 'react';

const RELOAD_TS_KEY = 'fakt-chunk-reload-ts';
const RELOAD_DEBOUNCE_MS = 10_000;

/**
 * Prueft ob ein Error ein Stale-Chunk-/Lazy-Import-Fehler ist.
 * Exportiert, weil die ErrorBoundary den gleichen Test braucht — wenn ein
 * Sub-Import in einem schon geladenen Chunk den MIME-Type-Fehler wirft,
 * landet er nicht im lazy()-Wrapper sondern im Render und damit in der
 * ErrorBoundary.
 */
export function isChunkLoadError(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  const msg = err.message;
  return (
    // Chrome/Edge/Firefox-Familie
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Loading chunk') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    // iOS Safari + macOS Safari: bei abgelaufener Chunk-URL liefert der Server
    // den SPA-Fallback (text/html) statt der erwarteten JS-Datei. Safari
    // verweigert das Laden mit MIME-Type-Fehler — ist semantisch dasselbe
    // wie ein Chunk-Load-Fail, hat aber komplett andere Wortwahl.
    msg.includes('is not a valid JavaScript MIME type') ||
    msg.includes('not a valid JavaScript MIME type') ||
    msg.includes('Expected a JavaScript module script') ||
    // Generischer Fallback: jeder Fehler der "module" + "script" enthaelt
    // und beim Lazy-Loading auftrat ist mit hoher Wahrscheinlichkeit ein
    // Stale-Chunk. Ein einmaliger Reload schadet nicht (Debounce verhindert Loop).
    (/\bmodule\b.*\bscript\b/i.test(msg) && /\bload(ing)?\b|fetch/i.test(msg))
  );
}

/**
 * Versucht einen Auto-Reload, wenn der Error wie ein Stale-Chunk aussieht
 * UND wir nicht schon vor weniger als 10 s reloaded haben (gegen Loop).
 *
 * Returnt true wenn ein Reload getriggert wurde — in dem Fall sollte der
 * Caller seine eigene Fehler-UI nicht zeigen, weil die Page eh gleich
 * neu lädt.
 */
export function tryAutoReloadOnChunkError(err: unknown): boolean {
  if (!isChunkLoadError(err)) return false;
  // Defensive gegen kaputte sessionStorage-Werte: parseInt('abc') liefert
  // NaN, jeder Vergleich mit NaN ist false → Reload würde NIE laufen.
  const stored = parseInt(sessionStorage.getItem(RELOAD_TS_KEY) ?? '0', 10);
  const lastReloadTs = Number.isFinite(stored) ? stored : 0;
  const now = Date.now();
  if (now - lastReloadTs <= RELOAD_DEBOUNCE_MS) return false;
  sessionStorage.setItem(RELOAD_TS_KEY, String(now));
  window.location.reload();
  return true;
}

// `ComponentType<any>` mirrors React's eigenes lazy()-Signature — unknown
// wäre strenger, würde aber Page-Komponenten mit Props-Type ablehnen.
export function lazyWithReload<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  T extends ComponentType<any>,
>(factory: () => Promise<{ default: T }>): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      return await factory();
    } catch (err) {
      if (tryAutoReloadOnChunkError(err)) {
        // Promise blockiert, bis der Reload greift — nichts mehr zu tun
        return new Promise(() => {
          /* never resolves */
        });
      }
      // Innerhalb Debounce oder kein Chunk-Pattern → ErrorBoundary soll fangen
      throw err;
    }
  });
}
